<template>
  <div>
    <div class="blocks">
      <div class="mobile-list">
        <div class="d-flex align-items-center justify-content-between mx-3 px-1">
          <div class="d-flex align-items-center">
            <span class="mod-icon-arrow-left mod-icon-back" @click="$router.back()" />
            <h2 class="ml-2">Totals</h2>
          </div>
          <div class="flex-grow-1 mr-3">
            <div class="input-group search w-100 ml-3">
              <button type="button" class="btn input-group-btn" @click="clearSearch">
                <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
              </button>
              <input class="form-control" type="text" v-model="q" @keyup="searchText($event)"
                     placeholder="Search"
              />
            </div>
          </div>
        </div>
        <div class="mt-3 border-bottom">
          <div class="d-flex align-items-center justify-content-between mx-3 px-1">
            <p class="text-secondary m-0">Statistics for {{ meta.total }} users</p>
            <div class="d-flex align-items-center mb-1">
              <button class="btn btn-primary btn-schedule btn-refine" @click="showFilters = true">
                <span v-if="!clearFiltersButtonShouldBeDisabled" class="mobile-dot-refine-active-filters" />
                REFINE
              </button>
              <button class="btn btn-secondary expand-all" @click="expanded = !expanded">
                <i :class="[expanded ? 'fa fa-compress' : 'fa fa-expand']" />
              </button>
            </div>
          </div>
        </div>

        <SalesTotalsBlockMobile v-for="sale in sales" :key="sale.id" :sale="sale" :expanded="expanded" @openSidebar="openSidebar(sale)" />
        <div class="row blocks">
          <pagination class="col-sm-6 mb-1 pr-0" :change-page="changePage" :meta="meta" />
        </div>
      </div>

      <div class="desktop-list">
        <div class="row pt-3 l-m-b-20 d-flex align-items-center">
          <div class="col-1">
            <h1>Totals</h1>
          </div>
          <div class="col-10 d-flex">
            <div class="input-group search">
              <button type="button" class="btn input-group-btn" @click="clearSearch">
                <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
              </button>
              <input class="form-control" type="text" v-model="q" @keyup="searchText($event)"
                     placeholder="Search"
              />
            </div>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-12 filters-field">
            <span>Filters:</span>
            <span v-if="filtersLoaded">
              <span v-if="filters.carrier.length">
                <span v-for="carrier in filters.carrier" :key="carrier" class="filter-item btn btn-filter">
                  <span class="mod-icon-filter" @click="removeFilter('carrier', carrier)" />
                  Carrier: {{ carriers.find(el => el.id === carrier).name }}</span>
              </span>
              <span v-if="filters.company.length">
                <span v-for="company in filters.company" :key="company" class="filter-item btn btn-filter">
                  <span class="mod-icon-filter" @click="removeFilter('company', company)" />
                  Company: {{ companies.find(el => el.id === company).name }}</span>
              </span>
              <span v-if="filters.created_at" class="filter-item btn btn-filter">
                <span class="mod-icon-filter" @click="removeFilter('created_at')" />
                Created at: {{ filters.created_at }}</span>
              <span v-if="filters.tags && filters.tags.length && tags">
                <span v-for="tag in filters.tags" :key="tag" class="filter-item btn btn-filter">
                  <span class="mod-icon-filter" @click="removeFilter('tags', tag)" />
                  Tag: {{ tags.find(el => el.id === tag).name }} </span>
              </span>
            </span>
            <span v-if="filters.deductedMoverWages" class="filter-item btn btn-filter">
              <span class="mod-icon-filter" @click="filters.deductedMoverWages = null" />
              Deducted mover wages
            </span>
            <button type="button" class="btn btn-secondary add-filters" @click="showFilters = true">
              <i class="fa fa-plus filter-icon" />
            </button>
          </div>
        </div>

        <template v-if="loading">
          <vue-content-loading :width="800" :height="450">
            <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
          </vue-content-loading>
        </template>
        <template v-else-if="!sales.length">
          <span>There are no results.</span>
        </template>
        <template v-else>
          <div class="sales-table">
            <SalesTotalsStatistics class="blocks" :meta="meta" :sums="sums" :averages="averages" />

            <SalesTableHeader :sort-key="filters.sortKey" :sort-order="filters.sortOrder" @sort-by="sortBy" />

            <div class="sales-wrapper blocks">
              <SalesTotalsBlock
                class="pointer"
                v-for="sale in sales"
                :key="sale.id"
                :sale="sale"
              />
            </div>
          </div>

          <div class="row blocks">
            <div class="col-sm-6 mb-1 text-black-50 pl-0"
                 v-if="meta.total"
            >
              Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
            </div>
            <pagination class="col-sm-6 mb-1 pr-0" :change-page="changePage" :meta="meta" />
          </div>
        </template>
      </div>
    </div>

    <!-- Filters -->
    <transition name="slide-fade">
      <filters-modal
        :clear-filters-button-should-be-disabled="clearFiltersButtonShouldBeDisabled"
        :carriers="carriers"
        :companies="companies"
        :filters="filters"
        :show-filters="showFilters"
        :tags="tags"
        @clear-filters="clearFilters"
        @filter="filter"
        @hide-filters="hideFilters"
        @search="search"
      />
    </transition>
  </div>
</template>

<script>
import toastr from 'toastr';
import { mapState } from 'vuex';
import 'flatpickr/dist/flatpickr.css';
import axios from 'axios';
import * as moment from 'moment';
import FileDownload from 'js-file-download';
import FiltersModal from './sales/FiltersModal';
import Errors from '../../classes/Errors';
import Pagination from '../Pagination';
import SalesTotalsBlock from './sales/SalesTotalsBlock';
import SalesTotalsBlockMobile from './sales/SalesTotalsBlockMobile';
import SalesTotalsStatistics from './sales/SalesTotalsStatistics';
import SalesTableHeader from './sales/SalesTableHeader';

export default {
  name: 'SalesTotals',
  components: {
    SalesTableHeader,
    FiltersModal,
    Pagination,
    SalesTotalsBlock,
    SalesTotalsBlockMobile,
    SalesTotalsStatistics,
  },
  data: () => ({
    averages: {},
    cancelToken: '',
    errors: new Errors(),
    expanded: false,
    filters: {
      carrier: [],
      company: [],
      createdAtEnd: moment()
        .subtract(1, 'month')
        .endOf('month')
        .format('M/D/YYYY'),
      createdAtStart: moment()
        .subtract(1, 'month')
        .startOf('month')
        .format('M/D/YYYY'),
      created_at: `${moment()
        .subtract(1, 'month')
        .startOf('month')
        .format('M/D/YYYY')} to ${moment()
        .subtract(1, 'month')
        .endOf('month')
        .format('M/D/YYYY')}`,
      sortKey: 'first_name',
      sortOrder: 'asc',
      tags: [],
    },
    inProgress: false,
    loading: false,
    meta: [],
    page: 1,
    q: '',
    sales: {},
    showFilters: false,
    source: '',
    sums: {},
    tags: [],
  }),
  computed: {
    ...mapState({
      carriers: state => state.carriers.carriers.data,
      companies: state => state.companies.companies.data,
      clusterUsersFilter: state => state.clusterUsers.clusterUsers,
      filtersLoaded: state => state.guard.filtersLoaded,
    }),
    clearFiltersButtonShouldBeDisabled() {
      return (
        !this.filters.carrier.length &&
        !this.filters.created_at &&
        !this.filters.company.length &&
        !this.filters.tags.length
      );
    },
  },
  created() {
    this.cancelToken = axios.CancelToken;
    this.source = this.cancelToken.source();
  },
  mounted() {
    this.getTags();
    if (Object.keys(this.$route.query).length !== 0) {
      this.setFilters(this.$route.query);
    }
    this.filter();
  },
  methods: {
    changePage(pageNum) {
      this.page = pageNum;
      this.filter();
    },
    clearFilters() {
      this.filters = Object.assign(
        {
          carrier: [],
          company: [],
          created_at: '',
          createdAtStart: '',
          createdAtEnd: '',
          sortKey: 'first_name',
          sortOrder: 'desc',
          tags: [],
        },
        {},
      );
      this.filter();
      this.showFilters = false;
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    downloadReport() {
      const query = `${this.getQuery()}&paginated=false`;
      axios({
        url: `sales/report?${query}`,
        method: 'GET',
        responseType: 'blob',
      }).then(response => {
        let fileName = `Totals ${moment().format('YYYY-MM-DD hhmmss')}`;
        // if (this.filters.carrier && this.filters.carrier.length) {
        //   const carrierName = this.carriers.find(carrier => carrier.id === this.filters.carrier)
        //     .name;
        //   fileName += ` ${carrierName}`;
        // } else if (this.filters.company && this.filters.company.length) {
        //   const companyName = this.companies.find(company => company.id === this.filters.company)
        //     .name;
        //   fileName += ` ${companyName}`;
        // }

        fileName += '.csv';

        FileDownload(response.data, fileName);
      });
    },
    filter() {
      const query = this.getQuery();
      if (!this.inProgress) {
        this.getTotalsFiltered(query);
        if (this.$_checkProperties(this.filters)) {
          this.$router.push(`/reports/sales?${query}`);
        } else {
          this.$router.push(`/reports/sales`);
        }
      } else {
        // If there is a request in progress, cancel it, and try again with other request
        this.source.cancel('Operation canceled by the user.');
        // Set axios cancel token and source to new values, so you can repeat request
        this.cancelToken = axios.CancelToken;
        this.source = this.cancelToken.source();
        this.inProgress = false;
        this.getTotalsFiltered(query);
        if (this.$_checkProperties(this.filters)) {
          this.$router.push(`/reports/sales?${query}`);
        } else {
          this.$router.push(`/reports/sales`);
        }
      }
    },
    formatToMilliseconds(h, m, s) {
      return (h * 60 * 60 + m * 60 + s) * 10;
    },
    getTags() {
      axios
        .get('/companies/tags/list?paginated=false')
        .then(res => {
          this.tags = res.data.data;
        })
        .catch(() => {});
    },
    getQuery() {
      return (
        `page=${this.page}&carrier_id=${this.filters.carrier}&company_id=${
          this.filters.company
        }&created_from=${this.filters.createdAtStart}` +
        `&created_to=${this.filters.createdAtEnd}&q=${encodeURIComponent(this.q)}` +
        `&sort=${this.filters.sortKey}&order=${this.filters.sortOrder}` +
        `&tag_id=${this.filters.tags}`
      );
    },
    getTotalsFiltered(query) {
      this.loading = true;
      this.inProgress = true;
      axios
        .get(`sales?${query}`, { cancelToken: this.source.token })
        .then(res => {
          this.averages = res.data.avg;
          this.meta = res.data.meta;
          this.sums = res.data.sums;
          this.sales = res.data.data;
        })
        .finally(() => {
          this.loading = false;
          this.inProgress = false;
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            toastr.error(err.message);
            this.loading = true;
          }
        });
    },
    hideFilters() {
      this.showFilters = false;
    },
    removeFilter(model, id) {
      if (id) {
        this.filters[model] = this.filters[model].filter(element => element !== id);
      }
      if (model === 'created_at') {
        this.filters.created_at = '';
        this.filters.createdAtStart = '';
        this.filters.createdAtEnd = '';
      }
      this.search();
    },
    search() {
      this.page = 1;
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    setFilters(query) {
      this.filters = Object.assign(
        {
          carrier: query.carrier_id ? query.carrier_id.split(',').map(i => Number(i)) : [],
          company: query.company_id ? query.company_id.split(',').map(i => Number(i)) : [],
          createdAtStart: query.created_from.toString(),
          createdAtEnd: query.created_to.toString(),
          created_at:
            query.created_from && query.created_to
              ? `${query.created_from.toString()} to ${query.created_from.toString()}`
              : '',
          tags: query.tag_id ? query.tag_id.split(',').map(i => Number(i)) : [],
          sortKey: query.sort || this.filters.sortKey,
          sortOrder: query.order || this.filters.sortOrder,
        },
        {},
      );
      this.q = query.q.toString();
      this.page = query.page;
    },
    sortBy(key) {
      this.$set(this.filters, 'sortKey', key);
      this.$set(this.filters, 'sortOrder', this.filters.sortOrder === 'desc' ? 'asc' : 'desc');
      this.filter();
    },
  },
};
</script>
<style lang="scss" scoped>
.desktop-list {
  display: block;
}

.mobile-list {
  display: none;
}
.sales-wrapper {
  height: calc(80vh - 112px);
  overflow: auto;
}
.input-group {
  width: 35%;
}
.sales-icon {
  color: #c0c6cc;
  font-size: 18px;
}
.truck-number,
.mover-number {
  font-size: 16px;
}
.balance-due {
  color: #ff2929;
}
.pricing-section {
  padding: 4px 13px 2px 13px;
  justify-content: space-between;
}

@media screen and (max-width: 992px) {
  .mobile-list {
    display: block;
  }

  .desktop-list {
    display: none;
  }

  .blocks {
    margin: 20px 0 20px 0;
  }

  .mobile-menu-link {
    width: 50%;
    color: #6c6c6c;
    text-align: center;
    font-weight: 400;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 5px;
  }

  .mobile-menu-link-active {
    color: #0f47e2;
    border-bottom: 2px solid #0f47e2;
  }

  .sale-table-title {
    white-space: nowrap;
    font-size: 0.67rem;
    font-weight: 500;
    color: black;
    text-align: right;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/reports/SalesTotals.vue